import { useState, RefObject, useRef } from "react";
import { getStringFromId } from "helpers/vernacular";

import { get } from "../logics/src/utils/helpers";
import { getIcons, ICONS_NAMES } from "../helpers/utils/getIcons";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { DEVICE_TYPE, platformNames } from "constants/index";
import { ISelectedItem, IUsePriceDetailsInterface } from "./@types";
import { ICartConvenienceChargesBreakupDto } from "store/payment/transformers/cart/shippingDataDTOTransformer";
import { getShippingChargeStriked, getShippingDetails } from "../logics/src/helper";
import { checkIsBeautyPlatform } from "helpers/utils/redirectToCheckoutPage";
import { formatNumber } from "helpers/utils/numberFormatter";
import { checkIsConvenienceChargesExperimentEnabled } from "../helpers";


export const usePriceDetailsConfig = ({
  theme,
  isGuest,
  cartInfo,
  callbacks,
  orderAmount,
  noMargin,
  deviceType,
  initialAccordianState,
  platform = platformNames.NYKAA,
  codHandlingCharge = 0,
  isCodMethodSelected = false,
}: IUsePriceDetailsInterface) => {
  const myRef = useRef(null);
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false);
  const [selectedList, setSelectedList] = useState<ISelectedItem>({
    title: "",
    value: 0,
    htmlText: "",
    isToolTipVisible: false,
    font: {
      fontWeight: 400,
      fontSize: "",
      lineHeight: "",
    },
    bottomSheetTitle: "",
    bottomSheetContent: "",
    color: "",
    showActionButton: false, // this is to manage button on bottom sheet.
  });
  const [isToolTipVisible, setIsToolTipVisible] = useState(false);
  const [accordian, setAccordian] = useState(initialAccordianState || false);

  const trackToggleAccordian = get(callbacks, "trackToggleAccordian");
  const trackInfoIconClick = get(callbacks, "trackInfoIconClick");
  const shippingCharge = get(cartInfo, "shippingCharge");
  const shippingText = get(cartInfo, "shippingText");
  const bagTotal = get(cartInfo, "bagTotal");
  const shippingConfig = get(cartInfo, "cartTotal.shipping");
  const discount = Math.round(get(cartInfo, "discount"));
  const totalSaved = Math.round(get(cartInfo, "totalSaved"));
  const bagDiscount = get(cartInfo, "bagDiscount");
  const paymentDetails = get(cartInfo, "paymentDetails");
  const itemCount: number = get(paymentDetails, "itemCount");
  const rewardsApplied = get(paymentDetails, "rewardsApplied");
  const rewardPointsTitle = get(paymentDetails, "rewardPointsTitle");
  const rewardPointsAmount = get(paymentDetails, "rewardPointsAmount");
  const conversionMultiplier = get(paymentDetails, "conversionMultiplier");
  const appliedOfferLabelDetails = get(paymentDetails, "appliedOfferLabelDetails");
  let rewardPointsRecieve: number = get(paymentDetails, "rewardPointsRecieve");
  const grandTotal = get(cartInfo, "grandTotal");
  /**
   * Applying cod charges when user selects cod option
   */
  const codCharges = isCodMethodSelected && codHandlingCharge && grandTotal ? codHandlingCharge : 0;
  const totalCharges = {
    title: "Total",
    amount: shippingCharge,
    border: "1px dashed rgba(0, 19, 37, 0.08)",
    font: theme?.typography?.titleSM,
    titleColor: theme?.colors?.bagTableColor,
    color: theme?.colors?.bagTableColor,
  };
  let cartConvenienceChargesBreakupDto = get(cartInfo, "cartConvenienceChargesBreakupDto") || [];
  /**
   * updating cartConvenienceChargesBreakupDto by adding
   * total amout i.e shippingCharge.
   */
  if (cartConvenienceChargesBreakupDto?.length) {
    cartConvenienceChargesBreakupDto = [...cartConvenienceChargesBreakupDto, totalCharges];
  }
  
  const getCartConvenienceAmountText = (amount: number, strikeThroughAmount: string | null) => {
    let value =
      amount > 0
        ? `₹${formatNumber(amount)}`
        : `<s>₹${formatNumber(strikeThroughAmount)}</s>&ensp;<div style="color:${theme.colors.notificationPositiveFill};">Free</div>`;

    if (amount != 0 && strikeThroughAmount) {
      value = `<s>₹${formatNumber(strikeThroughAmount)}</s>&ensp;₹${formatNumber(amount)}`;
    }

    return value;
  };

  /**
   * convenience chagre bottom sheet config.
   */
  const cartConvenienceBreakupConfig = cartConvenienceChargesBreakupDto?.map(
    (charges: ICartConvenienceChargesBreakupDto) => ({
      bottomSheetTitle: charges.title,
      htmlText: getCartConvenienceAmountText(parseInt(charges.amount), charges.strikeThroughAmount),
      font: charges.font || theme?.typography?.subTitleLG,
      color: theme.colors.brandPrimary,
      title: charges.title,
      subTitle: charges.subTitle,
      subTitleFont: theme.typography.bodySmall,
      subTitleColor: theme.colors.brandPrimary,
      border: charges.border || "",
    })
  );

  // Rounding of reward points FC-1089
  if (rewardPointsRecieve) {
    rewardPointsRecieve = Math.round(rewardPointsRecieve);
  }

  const gotItText = getStringFromId(
    VERNACULAR_STRING_IDS.GOT_IT,
    VERNACULAR_DEFAULT_MESSAGES.GOT_IT
  );
  let totalSavedTitle = getStringFromId(
    VERNACULAR_STRING_IDS.TOTAL_AMOUNT,
    VERNACULAR_DEFAULT_MESSAGES.TOTAL_AMOUNT
  );
  const rewardTitle = getStringFromId(
    VERNACULAR_STRING_IDS.REWARD_POINTS,
    VERNACULAR_DEFAULT_MESSAGES.REWARD_POINTS
  );

  let shippingChargeTitle = shippingConfig?.title || getStringFromId(
    VERNACULAR_STRING_IDS.SHIPPING_CHARGE,
    VERNACULAR_DEFAULT_MESSAGES.SHIPPING_CHARGE
  );
  const youPayText = getStringFromId(
    VERNACULAR_STRING_IDS.YOU_PAY,
    VERNACULAR_DEFAULT_MESSAGES.YOU_PAY
  );
  let shipppingTitle = shippingConfig?.title || getStringFromId(
    VERNACULAR_STRING_IDS.SHIPPING,
    VERNACULAR_DEFAULT_MESSAGES.SHIPPING
  );
  const cashHandlingFee = getStringFromId(
    VERNACULAR_STRING_IDS.CASH_HANDLING_FEE,
    VERNACULAR_DEFAULT_MESSAGES.CASH_HANDLING_FEE
  );
  const cashHandlingFeeMessage = getStringFromId(
    VERNACULAR_STRING_IDS.CASH_HANDLING_FEE_MESSAGE,
    VERNACULAR_DEFAULT_MESSAGES.CASH_HANDLING_FEE_MESSAGE
  );
  let rewardMessageCardText = getStringFromId(
    VERNACULAR_STRING_IDS.DYNAMIC_REWARD_MESSAGE,
    `Earn ${rewardPointsRecieve} Reward points`,
    { rewardPointsRecieve }
  );

  if (deviceType === DEVICE_TYPE.DESKTOP) {
    rewardMessageCardText = getStringFromId(
      VERNACULAR_STRING_IDS.DYNAMIC_REWARD_MESSAGE,
      `Earn ${rewardPointsRecieve} Reward points`,
      { rewardPointsRecieve }
    );
  }

  const rewardMessageCardSubtitle = getStringFromId(
    VERNACULAR_STRING_IDS.REWARD_MESSAGE_SUBTITLE,
    VERNACULAR_DEFAULT_MESSAGES.REWARD_MESSAGE_SUBTITLE
  );
  const shippingChargeStriked = getShippingChargeStriked();
  if (totalSaved > 0) {
    totalSavedTitle = getStringFromId(
      VERNACULAR_STRING_IDS.DYNAMIC_BAG_MRP,
      `Bag MRP (${itemCount} items)`,
      { itemCount }
    );
  }
  const rewardsTitle = rewardTitle || rewardPointsTitle;

  /**
   * for fashion, Shipping label inside payment details will be replaced with Convenience Charges and
   * Shipping Charge text on the bottomsheet will also be replaced with Convenience Charges
   */
  if (!checkIsBeautyPlatform() && platform !== platformNames.NIKE) {
    shippingChargeTitle = getStringFromId(
      VERNACULAR_STRING_IDS.CONVENIENCE_CHARGES,
      VERNACULAR_DEFAULT_MESSAGES.CONVENIENCE_CHARGES
    );
    shipppingTitle = getStringFromId(
      VERNACULAR_STRING_IDS.CONVENIENCE_CHARGES,
      VERNACULAR_DEFAULT_MESSAGES.CONVENIENCE_CHARGES
    );
  }

  const offerName = getStringFromId(
    VERNACULAR_STRING_IDS.OFFER_NAME,
    VERNACULAR_DEFAULT_MESSAGES.OFFER_NAME
  );
  const discountPrice = getStringFromId(
    VERNACULAR_STRING_IDS.DISCOUNT_PRICE,
    VERNACULAR_DEFAULT_MESSAGES.DISCOUNT_PRICE
  );
  const discountText = getStringFromId(
    VERNACULAR_STRING_IDS.DISCOUNT,
    VERNACULAR_DEFAULT_MESSAGES.DISCOUNT
  );
  const totalDiscountText = getStringFromId(
    VERNACULAR_STRING_IDS.TOTAL_DISCOUNT,
    VERNACULAR_DEFAULT_MESSAGES.TOTAL_DISCOUNT
  );
  const bagDiscountTitle = getStringFromId(
    VERNACULAR_STRING_IDS.BAG_DISCOUNT,
    VERNACULAR_DEFAULT_MESSAGES.BAG_DISCOUNT
  );
  const priceDetailsTitle = getStringFromId(
    VERNACULAR_STRING_IDS.PRICE_DETAILS,
    VERNACULAR_DEFAULT_MESSAGES.PRICE_DETAILS
  );

  const bottomText = getStringFromId(
    VERNACULAR_STRING_IDS.DYNAMIC_YOU_ARE_SAVING,
    `You are saving ₹${formatNumber(totalSaved || 0)}.`,
    { totalSaved: `${totalSaved}` }
  );
  let subTitle = getStringFromId(
    VERNACULAR_STRING_IDS.DYNAMIC_YOU_ARE_SAVING,
    `You are saving ₹${formatNumber(totalSaved || 0)}.`,
    { totalSaved: `${totalSaved}` }
  );

  if (deviceType === DEVICE_TYPE.DESKTOP) {
    subTitle = "";
  }

  // scroll to view when accordian opens
  const scrollToRef = (ref: RefObject<HTMLInputElement>) => {
    // 100 milliseconds for accordian to open and actual ref value is found
    setTimeout(() => {
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const toggleAccordian = () => {
    scrollToRef(myRef);
    if (trackToggleAccordian) trackToggleAccordian(!accordian);
    setAccordian(!accordian);
  };

  const toggleBottomSheet = () => {
    if (isBottomSheetVisible && isToolTipVisible) {
      setIsToolTipVisible(false);
    }
    setIsBottomSheetVisible(!isBottomSheetVisible);
  };

  const onToolTipClick = (selectedItem: ISelectedItem) => {
    if (selectedItem.toolTipTitle) {
      setIsToolTipVisible(!isToolTipVisible);
      return;
    }
    setSelectedList(selectedItem);
    // tracking for info icon click
    if (selectedItem.title === "Shipping" || selectedItem.title === "Discount") {
      if (trackInfoIconClick) trackInfoIconClick(selectedItem.title);
    }
    toggleBottomSheet();
  };

  const shippingObj = {
    title: shipppingTitle,
    value: shippingCharge,
    htmlText:
      shippingCharge > 0
        ? `₹${formatNumber(shippingCharge)}`
        : `<s>₹${formatNumber(shippingChargeStriked)}</s>&ensp;<div style="color:${theme.colors.notificationPositiveFill};">Free</div>`,
    isToolTipVisible: true,
    font: theme?.typography?.titleSM,
    titleColor: theme?.colors?.bagTableColor,
    color: theme?.colors?.bagTableColor,
    bottomSheetTitle: shippingChargeTitle,
    bottomSheetContent: getShippingDetails(cartConvenienceBreakupConfig, shippingText, checkIsConvenienceChargesExperimentEnabled() || false),
    //if we are having cartConvenienceBreakupDTO and convenience charge experiment is enabled hide action button
    showActionButton: cartConvenienceBreakupConfig.length && checkIsConvenienceChargesExperimentEnabled() ? false : true,
  };

  const codConvenienceObj = {
    title: cashHandlingFee,
    value: codCharges,
    htmlText:`₹${formatNumber(codCharges)}`,
    isToolTipVisible: true,
    bottomSheetTitle: cashHandlingFee,
    bottomSheetContent: cashHandlingFeeMessage,
    showActionButton: true,
    font: theme?.typography?.titleSM,
    titleColor: theme?.colors?.bagTableColor,
    color: theme?.colors?.bagTableColor,
  };

  let priceList = [
    {
      title: totalSavedTitle,
      value: bagTotal,
      htmlText: `₹${formatNumber(bagTotal || 0)}`,
      font: theme?.typography?.titleSM,
      titleColor: theme?.colors?.bagTableColor,
      color: theme?.colors?.bagTableColor,
    },
  ];

  let discountsList = [
    {
      title: offerName,
      value: 0,
      htmlText: discountPrice,
      isToolTipVisible: false,
      font: theme.typography.titleXSmall,
      bottomSheetTitle: "",
      bottomSheetContent: "" || [{}],
      color: theme.colors.notificationPositiveFill,
      showActionButton: false,
    },
  ];

  const priceListObj = {
    title: "",
    value: 0,
    htmlText: "",
    isToolTipVisible: false,
    font: theme?.typography?.titleSM,
    titleColor: theme?.colors?.bagTableColor,
    bottomSheetTitle: "",
    bottomSheetContent: "" || [{}],
    color: theme?.colors?.bagTableColor,
    showActionButton: false,
  };

  if (bagDiscount > 0) {
    const obj = Object.assign({}, priceListObj);
    obj.title = bagDiscountTitle;
    obj.value = bagDiscount;
    obj.htmlText = `-₹${formatNumber(bagDiscount || 0)}`;
    obj.color = theme.colors.notificationPositiveFill;
    priceList.push(obj);
  }

  if (discount > 0) {
    const obj = Object.assign({}, priceListObj);
    obj.title = discountText;
    obj.value = discount;
    obj.htmlText = `-₹${formatNumber(discount)}`;
    obj.isToolTipVisible = true;
    obj.showActionButton = true;
    obj.bottomSheetTitle = obj.title;
    obj.color = theme.colors.notificationPositiveFill;
    const discountListObj = {
      title: "",
      value: 0,
      htmlText: "",
      isToolTipVisible: false,
      font: theme?.typography?.titleSM,
      titleColor: theme?.colors?.bagTableColor,
      bottomSheetTitle: "",
      bottomSheetContent: "" || [{}],
      color: theme?.colors?.bagTableColor,
      isToolTipContentVisible: false,
      toolTipTitle: "",
      toolTipSubTitle: "",
      toolTipToggle: () => {},
      showActionButton: false,
    };

    if (Array.isArray(appliedOfferLabelDetails) && appliedOfferLabelDetails.length > 0) {
      appliedOfferLabelDetails.map(item => {
        const discountObj = Object.assign({}, discountListObj);
        discountObj.title = item.ruleLabel;
        discountObj.value = item.discountAmount;
        discountObj.htmlText = `-₹${formatNumber(Math.round(item.discountAmount))}`;
        discountObj.color = theme.colors.notificationPositiveFill;
        discountsList.push(discountObj);
      });
    }

    if (rewardsApplied > 0) {
      const discountObj = Object.assign({}, discountListObj);
      discountObj.title = `${rewardsTitle} | ${Math.round(
        rewardPointsAmount * (conversionMultiplier || 100)
      )} points`;
      discountObj.value = rewardPointsAmount;
      discountObj.htmlText = `-₹${formatNumber(rewardPointsAmount)}`;
      discountObj.color = theme.colors.notificationPositiveFill;
      discountObj.isToolTipVisible = conversionMultiplier && true; // show tootltip when conversionMultipier is coming from backend
      discountObj.showActionButton = conversionMultiplier && true;
      discountObj.isToolTipContentVisible = isToolTipVisible;
      discountObj.toolTipTitle = "Reward Points";
      discountObj.toolTipSubTitle = `${conversionMultiplier || 100} points = ₹1`;
      discountObj.toolTipToggle = () => setIsToolTipVisible(!isToolTipVisible);
      discountsList.push(discountObj);
    }

    const discountObj = Object.assign({}, discountListObj);
    discountObj.title = totalDiscountText;
    discountObj.value = discount;
    discountObj.htmlText = `₹${formatNumber(discount || 0)}`;
    discountObj.color = theme.colors.notificationPositiveFill;
    discountObj.font = theme.typography.titleXSmall;
    discountsList.push(discountObj);
    obj.bottomSheetContent = discountsList;
    priceList.push(obj);
  }

  priceList.push(shippingObj);
  if (codCharges) {
    priceList.push(codConvenienceObj);
  }

  if (orderAmount > 0) {
    const obj = Object.assign({}, priceListObj);
    obj.title = youPayText;
    obj.value = orderAmount + codCharges;
    obj.htmlText = `₹${formatNumber(orderAmount + codCharges || 0)}`;
    obj.font = theme.typography.subTitleLG;
    obj.titleColor = theme.colors.textPrimary;
    obj.color = theme.colors.textPrimary;
    priceList.push(obj);
  }

  const config = {
    title: priceDetailsTitle,
    leadingIcon: "",
    bottomText: totalSaved > 0 && bottomText,
    subTitle: totalSaved > 0 && subTitle,
    rightText: !accordian && orderAmount > 0 && `₹${formatNumber(orderAmount + codCharges || 0)}`,
    isAccordianOpened: accordian,
    toggleAccordian,
    endIcon: accordian
      ? getIcons(platform, ICONS_NAMES.ARROW_UP)
      : getIcons(platform, ICONS_NAMES.ARROW_DOWN),
    rewardMessage: {
      text: rewardMessageCardText,
      subTitle: rewardMessageCardSubtitle,
      icon: getIcons(platform, ICONS_NAMES.REWARD_POINT_ICON),
      success: false,
      leadingIcon: getIcons(platform, ICONS_NAMES.SUCCESS_ICON),
    },
    isInputVisible: false,
    isRewardMessageVisible: !isGuest && rewardPointsRecieve > 0,
    priceList,
    isBottomSheetVisible,
    bottomSheetIcon: getIcons(platform, ICONS_NAMES.CLEAR),
    onBottomSheetClose: toggleBottomSheet,
    onBottomSheetIconClick: toggleBottomSheet,
    bottomSheetMaxHeight: 85,
    bottomSheetActionButtonText: gotItText,
    bottomSheetActionButtonClick: toggleBottomSheet,
    bottomSheetTitle: selectedList.bottomSheetTitle,
    bottomSheetContent: selectedList.bottomSheetContent,
    showActionButton: selectedList.showActionButton,
    onToolTipClick,
    isToolTipContentVisible: isToolTipVisible,
    noMargin,
    theme,
    setAccordian,
    myRef,
  };
  return config;
};
